import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Contract } from 'src/app/contract/shared/contract.model';
import { ContractService } from 'src/app/contract/shared/contract.service';
import { Approver } from 'src/app/shared/approver.model';
import { UserService } from 'src/app/shared/user.service';
import { AddTeamDialogComponent } from '../../add-team-dialog/add-team-dialog.component';
import { DeleteDialogComponent } from 'src/app/contract/contract-master/delete-dialog/delete-dialog.component';
import { RejectReasonDialogComponent } from '../reject-reason-dialog/reject-reason-dialog.component';
import { ViewNotesDialogComponent } from '../contract-notes/view-notes-dialog/view-notes-dialog.component';
 
@Component({
  selector: 'app-contract-approvals',
  templateUrl: './contract-approvals.component.html',
  styleUrls: ['./contract-approvals.component.scss']
})
export class ContractApprovalsComponent implements OnInit {
  @Input() contractId;
  @Input() editable;
  @Input() mode;
  @Output() refreshContract = new EventEmitter<void>();
  contract: Contract;
  approvers: Approver[];
  dataLoading: boolean = true;
  isLoading: boolean = false;
  isToggleLoading: { [key: string]: boolean } = {};
 
  constructor(public contractService: ContractService, public userService: UserService, private router: Router, private route: ActivatedRoute, private toast: ToastrService, public dialog: MatDialog) {
 
  }
 
  async ngOnInit() {
    await this.contractService.refreshContract(parseInt(this.contractId)).then((res: Contract) => {
      this.contract = res;
    });
 
    this.refreshApprover();
  }
 
  async toggleApproverStatus(id) {
    this.isToggleLoading[id] = true;
    await this.contractService.toggleApprover(id).then(() => {
      this.ngOnInit();
    })
    this.isToggleLoading[id] = false;
  }
 
  openAddApprover(): void {
    const dialogRef =
      this.dialog.open(AddTeamDialogComponent, {
        enterAnimationDuration: '400ms',
        exitAnimationDuration: '500ms',
        width: '30%',
        disableClose: true,
        data: {
          refresh: () => this.refreshApprover(),
          contractId: this.contractId,
          mode: 2
        }
      });
  }
 
  openRejectReason(approver): void {
    const dialogRef =
      this.dialog.open(RejectReasonDialogComponent, {
        enterAnimationDuration: '400ms',
        exitAnimationDuration: '500ms',
        width: '40%',
        maxWidth: '90vw',
        data: {
          reason: approver.description,
          contractId: this.contractId,
          userId: approver.approverId || approver.userInfo?.userId,
          approverId: approver.approverId
        }
      });
  }
 
  openDeleteConfirmation(id): void {
    const dialogRef =
      this.dialog.open(DeleteDialogComponent, {
        enterAnimationDuration: '400ms',
        exitAnimationDuration: '500ms',
        data: {
          deleteFunction: () => this.delete(id, dialogRef)
        }
      });
  }
 
  async delete(id: number, dialogRef: MatDialogRef<DeleteDialogComponent>) {
    try {
      await this.contractService.deleteApprover(id);
      this.toast.success('The user has been deleted successfully!', '');
      this.refreshApprover();
      dialogRef.close();
    } catch (error) {
      this.toast.error('Failed to delete the approver!', 'Error');
    }
  }
 
  async recall() {
    this.isLoading = true;
    try {
      await this.contractService.updateContractStatus(this.contractId, 1, null, null).then(() => {
        this.toast.success('Contract Recalled Successfully!', '');
      })
      this.ngOnInit();
      this.refreshContract.emit();
    } catch {
      this.toast.error('Something went wrong, Please try again later!', '')
    }
    this.isLoading = false;
  }
 
  async refreshApprover() {
    this.dataLoading = true;
    const contractId = Number(this.contractId);
    try {
      this.contract = await this.contractService.refreshContract(contractId) as Contract;
      this.approvers = await this.contractService.getAllContractApprovers(contractId) as Approver[];
      this.refreshContract.emit();
    } catch (error) {
      this.toast.error('Failed to load approvers. Please try again later!', '');
    } finally {
      this.dataLoading = false;
    }
  }
 
  async sentForApproval() {
    this.isLoading = true;
    try {
      const lowestSequenceApprover = this.approvers.reduce((minApprover: Approver | null, currentApprover: Approver) => {
        if (minApprover === null || currentApprover.sequenceNo < minApprover.sequenceNo) {
          return currentApprover;
        } else {
          return minApprover;
        }
      }, null);
      // With no approvers the contract goes straight to Approved, and there is
      // no approver to notify, so never dereference the null reduce result.
      await this.contractService.updateContractStatus(this.contractId, lowestSequenceApprover ? 2 : 3, lowestSequenceApprover?.approverId ?? null, 'Approval Submission').then(() => {
        this.toast.success('Contract is sent for Approval!', '');
        
      })
      this.ngOnInit();
      this.refreshContract.emit();
    } catch {
      this.toast.error('Something went wrong, Please try again later!', '')
    }
 
    // this.contract.completedStep = (this.contract.completedStep > 4) ? this.contract.completedStep : 4;
    // await this.contractService.updateContract(this.contract, this.contractId).then((res) => {
 
    // })
    this.isLoading = false;
  }

  get showBypassApproverNotice(): boolean {
    return !this.dataLoading && this.contract?.statusId === 3 && (!this.approvers || this.approvers.length === 0);
  }

  get showApproverEmptyState(): boolean {
    return !this.dataLoading && (!this.approvers || this.approvers.length === 0) && !this.showBypassApproverNotice;
  }

  openViewNotes(approver: Approver): void {
    const dialogRef = this.dialog.open(ViewNotesDialogComponent, {
      enterAnimationDuration: '400ms',
      exitAnimationDuration: '500ms',
      width: '700px',
      maxWidth: '90vw',
      disableClose: false,
      autoFocus: false,
      panelClass: 'view-notes-dialog',
      data: {
        contractId: this.contractId,
        userId: approver.approverId || approver.userInfo?.userId,
        userInfo: approver.userInfo,
        userName: `${approver.userInfo?.firstName || ''} ${approver.userInfo?.lastName || ''}`.trim()
      }
    });
  }
}
 